import React from "react";
import { Box, Card, CircularProgress, Divider } from "@mui/joy";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import { useHRData } from "../../contexts/app/hrData";
import { useLeave } from "../../contexts/api";
import { useAuth } from "../../contexts/api";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import type { UserWithRole } from "../../api/auth/Auth.typed";
import type {
  LeaveRequest,
  LeaveRequestSortBy,
  UserLeaveTypeBalance,
} from "../../api/leave/Leave.typed";
import { LeaveStatus } from "../../api/leave/Leave.typed";
import Pagination from "../common/Pagination";
import DarkTypography from "../common/DarkTypography";
import { formatDate as formatDateHelper, buildLeaveQuery } from "../../utils/leaveHelpers";
import {
  DEFAULT_PAGINATION_LIMIT,
  extractDataFromResponse,
  extractPaginationMetadata,
} from "../../utils/paginationHelpers";
import LeaveValidationFilters from "./display/LeaveValidationFilters";
import LeaveValidationRequestsTable from "./display/LeaveValidationRequestsTable";
import LeaveValidationBalancesBlock from "./display/LeaveValidationBalancesBlock";
import LeaveReasonModal from "./display/LeaveReasonModal";
import { NO_ACCESS_MESSAGE } from "../common/DashboardPage";
import { hrCardSx } from "../../styles/globalStyles";

const statusOptions = [
  { id: -1, name: "Tous les statuts" },
  { id: LeaveStatus.PENDING, name: "En attente" },
  { id: LeaveStatus.APPROVED, name: "Approuvé" },
  { id: LeaveStatus.REJECTED, name: "Refusé" },
  { id: LeaveStatus.CANCELLED, name: "Annulé" },
  { id: LeaveStatus.CANCELLATION_REQUESTED, name: "Demande d'annulation" },
];

export default function LeaveValidationPage() {
  const { getLeaveRequests, updateLeaveRequest, listBalancesByUser, setLeaveTypeBalance } =
    useLeave();
  const { leaveTypes } = useHRData();
  const modal = useConfirmationModal();
  const { getUsers, getUserName, refreshUsersMap } = useAuth();
  const { hasPermission } = usePermissions();
  const canAccessValidation =
    hasPermission(Permission.LEAVE_ACCESS) && hasPermission(Permission.LEAVE_VALIDATE);

  const [requests, setRequests] = React.useState<LeaveRequest[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [totalRequests, setTotalRequests] = React.useState(0);
  const [limit, setLimit] = React.useState(DEFAULT_PAGINATION_LIMIT);
  const [selectedUserId, setSelectedUserId] = React.useState<number>(-1);
  const [statusFilter, setStatusFilter] = React.useState<number>(-1);
  const [typeFilter, setTypeFilter] = React.useState<number>(-1);
  const [employees, setEmployees] = React.useState<UserWithRole[]>([]);
  const [reasonOpen, setReasonOpen] = React.useState(false);
  const [reasonText, setReasonText] = React.useState("");
  const [balances, setBalances] = React.useState<UserLeaveTypeBalance[]>([]);
  const [openCommentsForType, setOpenCommentsForType] = React.useState<number | null>(null);
  const [openComments, setOpenComments] = React.useState<
    Record<number, UserLeaveTypeBalance["MultiComments"]>
  >({});

  const [sortBy, setSortBy] = React.useState<LeaveRequestSortBy>("start_date_desc");
  const [yearFilter, setYearFilter] = React.useState<number>(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = React.useState<number>(-1);

  React.useEffect(() => {
    if (!canAccessValidation) {
      setEmployees([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      // Full directory feeds the name map (requesters), employees only feed the picker.
      const [directory, salaried] = await Promise.all([getUsers(), getUsers(true)]);
      if (cancelled) return;
      if (Array.isArray(directory)) await refreshUsersMap(directory);
      if (!cancelled) setEmployees(Array.isArray(salaried) ? salaried : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccessValidation, getUsers, refreshUsersMap]);

  React.useEffect(() => {
    if (!canAccessValidation) {
      setBalances([]);
      return;
    }
    (async () => {
      if (selectedUserId === -1) {
        setBalances([]);
        return;
      }
      const paged = await listBalancesByUser(selectedUserId, 1, 1000);
      setBalances(paged?.data ?? []);
      const map: Record<number, UserLeaveTypeBalance["MultiComments"]> = {};
      (paged?.data ?? []).forEach((b) => {
        map[b.TypeID] = b.MultiComments;
      });
      setOpenComments(map);
    })();
  }, [canAccessValidation, selectedUserId, listBalancesByUser]);

  const loadRequests = React.useCallback(async () => {
    if (!canAccessValidation) {
      setRequests([]);
      setTotalRequests(0);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Compute date range from year + month selectors
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (monthFilter !== -1) {
      const firstDay = new Date(Date.UTC(yearFilter, monthFilter - 1, 1));
      const lastDay = new Date(Date.UTC(yearFilter, monthFilter, 0, 23, 59, 59));
      startDate = firstDay.toISOString();
      endDate = lastDay.toISOString();
    } else {
      const firstDay = new Date(Date.UTC(yearFilter, 0, 1));
      const lastDay = new Date(Date.UTC(yearFilter, 11, 31, 23, 59, 59));
      startDate = firstDay.toISOString();
      endDate = lastDay.toISOString();
    }

    const query = buildLeaveQuery({
      page,
      limit,
      statusFilter,
      typeFilter,
      userId: selectedUserId !== -1 ? selectedUserId : undefined,
      startDate,
      endDate,
      sortBy,
    });
    const response = await getLeaveRequests(query);
    const requestsData = extractDataFromResponse<LeaveRequest>(response);
    setRequests(requestsData);
    const metadata = extractPaginationMetadata(response);
    if (metadata) setTotalRequests(metadata.total);
    else setTotalRequests(requestsData.length);
    setLoading(false);
  }, [
    canAccessValidation,
    getLeaveRequests,
    page,
    limit,
    statusFilter,
    typeFilter,
    selectedUserId,
    sortBy,
    yearFilter,
    monthFilter,
  ]);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, selectedUserId, sortBy, yearFilter, monthFilter]);

  const handleQuickValidation = React.useCallback(
    async (requestId: number, status: number) => {
      const verb = status === LeaveStatus.APPROVED ? "Valider" : "Refuser";
      const ok = await modal.showConfirmation(
        `${verb} la demande ?`,
        status === LeaveStatus.APPROVED
          ? "Cette demande sera marquée comme approuvée."
          : "Cette demande sera marquée comme refusée.",
      );
      if (!ok) return;
      await updateLeaveRequest(requestId, { Status: status });
      loadRequests();
    },
    [loadRequests, modal, updateLeaveRequest],
  );

  const handleSoftCancel = React.useCallback(
    async (requestId: number) => {
      const ok = await modal.showConfirmation(
        "Annuler la demande ?",
        "La demande approuvée sera annulée.",
      );
      if (!ok) return;
      await updateLeaveRequest(requestId, { Status: LeaveStatus.CANCELLED });
      loadRequests();
    },
    [loadRequests, modal, updateLeaveRequest],
  );

  const handleApproveCancellation = React.useCallback(
    async (requestId: number) => {
      const ok = await modal.showConfirmation(
        "Approuver l'annulation ?",
        "La demande sera marquée comme annulée.",
      );
      if (!ok) return;
      await updateLeaveRequest(requestId, { Status: LeaveStatus.CANCELLED });
      loadRequests();
    },
    [loadRequests, modal, updateLeaveRequest],
  );

  const handleRejectCancellation = React.useCallback(
    async (requestId: number) => {
      const ok = await modal.showConfirmation(
        "Refuser l'annulation ?",
        "La demande restera approuvée.",
      );
      if (!ok) return;
      await updateLeaveRequest(requestId, { Status: LeaveStatus.APPROVED });
      loadRequests();
    },
    [loadRequests, modal, updateLeaveRequest],
  );

  const handleSaveComments = React.useCallback(
    async (typeId: number, userId: number, updated: UserLeaveTypeBalance["MultiComments"]) => {
      await setLeaveTypeBalance(typeId, userId, { MultiComments: updated ?? [] });
      const paged = await listBalancesByUser(userId, 1, 1000);
      setBalances(paged?.data ?? []);
      const next = (paged?.data ?? []).find((x) => x.TypeID === typeId)?.MultiComments;
      setOpenComments((prev) => ({ ...prev, [typeId]: next }));
    },
    [listBalancesByUser, setLeaveTypeBalance],
  );

  const handleOpenReason = React.useCallback((text: string) => {
    setReasonText(text);
    setReasonOpen(true);
  }, []);

  const handleCloseReason = React.useCallback(() => {
    setReasonOpen(false);
  }, []);

  const handleItemsPerPageChange = React.useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const getUserNameStable = React.useCallback(
    (id: number) => getUserName(id) ?? `Utilisateur #${id}`,
    [getUserName],
  );

  if (!canAccessValidation) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: "50vh",
          p: 3,
        }}
      >
        <DarkTypography
          level="body-lg"
          colorVariant="muted"
          sx={{ textAlign: "center", maxWidth: 400 }}
        >
          {NO_ACCESS_MESSAGE}
        </DarkTypography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: 0,
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        p: { xs: 1, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <LeaveValidationFilters
          employees={employees}
          leaveTypes={leaveTypes}
          statusOptions={statusOptions}
          selectedUserId={selectedUserId}
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          onEmployeeChange={setSelectedUserId}
          onStatusChange={setStatusFilter}
          onTypeChange={setTypeFilter}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          yearFilter={yearFilter}
          monthFilter={monthFilter}
          onYearChange={setYearFilter}
          onMonthChange={setMonthFilter}
        />

        {requests.length === 0 ? (
          <Card sx={{ p: 4, textAlign: "center", ...hrCardSx }}>
            <DarkTypography level="body-lg">
              Aucune demande trouvée avec les filtres appliqués.
            </DarkTypography>
          </Card>
        ) : (
          <>
            <LeaveValidationRequestsTable
              requests={requests}
              leaveTypes={leaveTypes}
              getUserName={getUserNameStable}
              formatDate={formatDateHelper}
              onApprove={(id) => handleQuickValidation(id, LeaveStatus.APPROVED)}
              onReject={(id) => handleQuickValidation(id, LeaveStatus.REJECTED)}
              onCancel={handleSoftCancel}
              onApproveCancellation={handleApproveCancellation}
              onRejectCancellation={handleRejectCancellation}
              onOpenReason={handleOpenReason}
              canValidate={canAccessValidation}
            />

            {selectedUserId !== -1 && (
              <LeaveValidationBalancesBlock
                balances={balances}
                leaveTypes={leaveTypes}
                openCommentsForType={openCommentsForType}
                openComments={openComments}
                setOpenCommentsForType={setOpenCommentsForType}
                setOpenComments={setOpenComments}
                onSaveComments={handleSaveComments}
              />
            )}

            <Divider sx={{ my: 2 }} />
          </>
        )}
      </Box>

      {requests.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(totalRequests / limit)}
            totalItems={totalRequests}
            itemsPerPage={limit}
            onPageChange={setPage}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </Box>
      )}

      <LeaveReasonModal open={reasonOpen} reasonText={reasonText} onClose={handleCloseReason} />
    </Box>
  );
}
